import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaView1Service } from '../service';
import { GetMultiLocationFeatureSettingsIO } from '../types';

export const getGetMultiLocationFeatureSettingsQueryKey = (req: GetMultiLocationFeatureSettingsIO['input']) =>
  ['ViewV1', 'GetMultiLocationFeatureSettings', req] as const;
type QueryKey = ReturnType<typeof getGetMultiLocationFeatureSettingsQueryKey>;

/**
 * A hook that returns a query for the `GetMultiLocationFeatureSettings` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetMultiLocationFeatureSettingsQuery = <E = unknown, D = GetMultiLocationFeatureSettingsIO['output']>(
  req: GetMultiLocationFeatureSettingsIO['input'],
  options?: LimitedSchemaQueryOptions<GetMultiLocationFeatureSettingsIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetMultiLocationFeatureSettingsIO['output'], E, D, QueryKey>({
    queryKey: getGetMultiLocationFeatureSettingsQueryKey(req),
    queryFn: () => SchemaView1Service.GetMultiLocationFeatureSettings(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetMultiLocationFeatureSettings` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetMultiLocationFeatureSettingsQueries = <E = unknown, D = GetMultiLocationFeatureSettingsIO['output']>(
  argsArr: {
    req: GetMultiLocationFeatureSettingsIO['input'];
    options?: LimitedSchemaQueryOptions<GetMultiLocationFeatureSettingsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetMultiLocationFeatureSettingsQueryKey(req),
      queryFn: () => SchemaView1Service.GetMultiLocationFeatureSettings(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetMultiLocationFeatureSettingsQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetMultiLocationFeatureSettings` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetMultiLocationFeatureSettings = () => {
  const queryClient = useQueryClient();

  const fetchGetMultiLocationFeatureSettings = async <E = unknown, D = GetMultiLocationFeatureSettingsIO['output']>(
    req: GetMultiLocationFeatureSettingsIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetMultiLocationFeatureSettingsIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetMultiLocationFeatureSettingsIO['output'], E, D, QueryKey>(
      getGetMultiLocationFeatureSettingsQueryKey(req),
      () => SchemaView1Service.GetMultiLocationFeatureSettings(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetMultiLocationFeatureSettings;
};
